// material
import { experimentalStyled as styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from '../../redux/store';
import { getToNewUser } from '../../redux/slices/appliances';
import webServices from '../../services/webServices';

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden'
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

// ----------------------------------------------------------------------

// Category display order (card 108), shared with the proactive move page.
const CATEGORY_ORDER = ['Utilities', 'Appliances', 'Interior', 'Exterior', 'Lawn & Leisure', 'Furniture'];

// Move workflow (card 166): the departing owner clicked "I moved" in the claim
// email. Before ownership transfers to the new resident, they decide per item
// whether it stays with the residence or goes with them. Reconciled onto the
// service collection so it matches the in-app move page.
export default function NewUserLayout() {
  const params = useParams();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { success, error } = useSelector((state) => state.appliances);

  const [items, setItems] = useState([]);
  const [stayMap, setStayMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (success) enqueueSnackbar(success, { variant: 'success' });
  }, [success, enqueueSnackbar]);

  useEffect(() => {
    if (error) enqueueSnackbar(error, { variant: 'error' });
  }, [error, enqueueSnackbar]);

  // Load the owner's items for the stay/go checklist.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await webServices.getMoveItems({ token: params.token });
        if (!active) return;
        const list = resp && resp.success ? resp.data || [] : [];
        setItems(list);
        const initial = {};
        list.forEach((it) => {
          initial[it.id] = it.remainWithResidence !== false;
        });
        setStayMap(initial);
      } catch (err) {
        if (active) enqueueSnackbar('Could not load your items.', { variant: 'error' });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [params.token, enqueueSnackbar]);

  const toggle = (id) => setStayMap((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleComplete = () => {
    const dispositions = items.map((it) => ({
      serviceId: it.id,
      remainWithResidence: !!stayMap[it.id]
    }));
    dispatch(getToNewUser({ token: params.token, dispositions }));
    setSubmitted(true);
  };

  const stayCount = items.filter((it) => stayMap[it.id]).length;

  const grouped = CATEGORY_ORDER.map((category) => ({
    category,
    rows: items.filter((it) => it.category === category)
  })).filter((g) => g.rows.length > 0);

  return (
    <RootStyle>
      <MainStyle>
        <Container maxWidth="sm">
          <Card>
            <CardContent>
              <Typography variant="h4" gutterBottom>
                Confirm your move
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                For each item below, choose whether it stays with the home for the next resident or moves with you.
                Items you keep will be removed from this address.
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {loading && <Typography>Loading your items…</Typography>}

              {!loading && submitted && (
                <Typography>Thanks — your move has been completed. You can close this page.</Typography>
              )}

              {!loading && !submitted && items.length === 0 && (
                <Stack spacing={2}>
                  <Typography>You have no items to move.</Typography>
                  <Button variant="contained" onClick={handleComplete}>
                    Complete move
                  </Button>
                </Stack>
              )}

              {!loading && !submitted && items.length > 0 && (
                <Stack spacing={2}>
                  {grouped.map((group) => (
                    <Box key={group.category}>
                      <CardHeader
                        title={group.category}
                        sx={{ px: 0, pt: 0, pb: 1 }}
                        titleTypographyProps={{ variant: 'subtitle1' }}
                      />
                      <Stack divider={<Divider />}>
                        {group.rows.map((it) => (
                          <Box
                            key={it.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              py: 1
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle2">{it.name || 'Item'}</Typography>
                              {it.brand && (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {it.brand}
                                </Typography>
                              )}
                            </Box>
                            <FormControlLabel
                              control={<Switch checked={!!stayMap[it.id]} onChange={() => toggle(it.id)} />}
                              label={stayMap[it.id] ? 'Stays with home' : 'Goes with me'}
                              labelPlacement="start"
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {stayCount} of {items.length} staying with the home.
                  </Typography>
                  <Button variant="contained" onClick={handleComplete}>
                    Complete move
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Container>
      </MainStyle>
    </RootStyle>
  );
}
